import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { FileText, Download, Loader2, Lock, ArrowRight, Sparkles, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReportsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);

  const userPlan = user?.plan || "trial";
  const canDownload = ["growth", "agency", "enterprise"].includes(userPlan);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await axios.get(`${API}/analyses`, { withCredentials: true });
        setAnalyses(data.filter(a => a.status === "completed"));
      } catch {}
      setLoading(false);
    };
    fetch();
  }, []);

  const handleDownload = async (analysis) => {
    if (!canDownload) {
      setShowUpgrade(true);
      return;
    }
    setDownloading(analysis.id);
    try {
      const response = await axios.get(`${API}/analyses/${analysis.id}/report/pdf`, {
        responseType: "blob", withCredentials: true
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `AdBit_Report_${analysis.ad_name || "report"}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.status === 403) {
        setShowUpgrade(true);
      } else {
        alert("Failed to download report. Please try again.");
      }
    }
    setDownloading(null);
  };

  const scoreColor = (s) => {
    if (s >= 80) return "text-emerald-600 bg-emerald-50";
    if (s >= 50) return "text-amber-600 bg-amber-50";
    return "text-red-500 bg-red-50";
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 bg-slate-200 rounded-lg animate-pulse" />
        {[1,2,3].map(i => <div key={i} className="h-20 bg-white rounded-2xl animate-pulse" />)}
      </div>
    );
  }

  return (
    <div className="max-w-4xl" data-testid="reports-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>Reports</h1>
          <p className="text-sm text-slate-500 mt-1">Download branded PDF reports of your analyses</p>
        </div>
        {!canDownload && (
          <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-xl px-3 py-2">
            <Lock size={14} className="text-orange-500" />
            <span className="text-xs text-orange-700 font-medium">PDF reports available from Growth plan</span>
          </div>
        )}
      </div>

      {/* Upgrade modal */}
      {showUpgrade && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowUpgrade(false)}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-2xl text-center" onClick={e => e.stopPropagation()}>
            <div className="w-14 h-14 rounded-2xl bg-orange-50 flex items-center justify-center mx-auto mb-4">
              <Sparkles size={24} className="text-orange-500" />
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
              Upgrade to Download PDF Reports
            </h3>
            <p className="text-sm text-slate-500 mb-6">
              PDF reports are available starting from the Growth plan. Upgrade to download branded, shareable reports.
            </p>
            <Button onClick={() => navigate("/#pricing")} className="gradient-btn rounded-xl w-full h-11 font-semibold text-sm">
              <span className="flex items-center gap-2">View Plans <ArrowRight size={14} /></span>
            </Button>
            <button onClick={() => setShowUpgrade(false)} className="text-xs text-slate-400 mt-3 block mx-auto hover:text-slate-600">
              Maybe later
            </button>
          </div>
        </div>
      )}

      {analyses.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
          <FileText size={40} className="text-slate-300 mx-auto mb-4" />
          <h3 className="text-base font-bold text-slate-700 mb-1">No completed analyses yet</h3>
          <p className="text-sm text-slate-500 mb-4">Run your first analysis to generate a report</p>
          <Button onClick={() => navigate("/analyze/new")} className="gradient-btn rounded-xl text-sm font-semibold">
            New Analysis <ArrowRight size={14} className="ml-1" />
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {analyses.map(a => {
            const score = a.results?.neuro_score || 0;
            return (
              <div key={a.id} className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center justify-between hover:border-slate-300 transition-all">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${scoreColor(score)}`}>
                    <span className="text-sm font-bold" style={{ fontFamily: "JetBrains Mono" }}>{score}</span>
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-bold text-slate-800 truncate">{a.ad_name || "Untitled"}</h3>
                    <p className="text-xs text-slate-500">
                      {a.category} · {a.platform} · {a.created_at?.slice(0, 10)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                  <Button variant="outline" size="sm" className="rounded-lg text-xs h-8"
                    onClick={() => navigate(`/analyze/${a.id}`)}>
                    <ExternalLink size={12} className="mr-1" /> View
                  </Button>
                  <Button size="sm"
                    className={`rounded-lg text-xs h-8 ${canDownload ? "gradient-btn" : "bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-50"}`}
                    disabled={downloading === a.id}
                    onClick={() => handleDownload(a)}>
                    {downloading === a.id ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : canDownload ? (
                      <><Download size={12} className="mr-1" /> PDF</>
                    ) : (
                      <><Lock size={12} className="mr-1" /> PDF</>
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
